import * as React from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { DayPicker } from 'react-day-picker';
import { cn } from '@noor/lib/cn';
import { buttonVariants } from './button';

/**
 * Calendar primitive on top of react-day-picker (v10). Presentational only;
 * FormField.DatePicker wires it to RHF.
 */
function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: React.ComponentProps<typeof DayPicker>) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn('p-3', className)}
      classNames={{
        // `root` is the only positioned ancestor the nav can anchor to: in
        // v10 `nav` is a direct child of root, so without `relative` here the
        // absolute arrows escape to the viewport and land nowhere near the
        // calendar — visible but unhittable, which reads as "stuck on the
        // current month". `w-fit` keeps the root from stretching in the
        // popover so the arrows track the grid's real width.
        root: 'relative w-fit',
        months: 'flex flex-col sm:flex-row gap-2',
        month: 'flex flex-col gap-4',
        // `pointer-events-none` is load-bearing. The caption is a FULL-WIDTH
        // `relative` box on the same row as the absolutely-positioned `nav`,
        // and it comes LATER in the DOM — so with both at `z-auto` the caption
        // paints over the arrows and swallows every click on them. The arrows
        // were on-screen and enabled but inert, which reads as "the calendar
        // is stuck on this month". The label itself needs no pointer events;
        // the nav re-enables them for its own buttons.
        month_caption:
          'pointer-events-none flex justify-center pt-1 relative items-center w-full',
        caption_label: 'text-sm font-medium',
        // Pinned to the caption row (root has `p-3`, caption is 28px tall) so
        // the arrows sit either side of the month label instead of below it.
        // `z-10` + `pointer-events-auto` put the arrows back above the caption
        // (see `month_caption`) and restore clicks the caption's
        // `pointer-events-none` would otherwise not have taken away from it.
        nav: 'absolute top-3 inset-x-3 z-10 flex h-7 items-center justify-between pointer-events-auto',
        button_previous: cn(
          buttonVariants({ variant: 'outline' }),
          'size-7 bg-transparent p-0 opacity-50 hover:opacity-100',
        ),
        button_next: cn(
          buttonVariants({ variant: 'outline' }),
          'size-7 bg-transparent p-0 opacity-50 hover:opacity-100',
        ),
        month_grid: 'w-full border-collapse space-x-1',
        weekdays: 'flex',
        weekday: 'text-muted-foreground rounded-md w-8 font-normal text-[0.8rem]',
        week: 'flex w-full mt-2',
        day: cn(
          'relative p-0 text-center text-sm focus-within:relative focus-within:z-20',
          '[&:has([aria-selected])]:bg-accent [&:has([aria-selected].day-range-end)]:rounded-r-full',
          '[&:has([aria-selected].day-outside)]:bg-accent/50 first:[&:has([aria-selected])]:rounded-l-full last:[&:has([aria-selected])]:rounded-r-full',
          '[&:has(.day-range-start)]:rounded-l-full [&:has(.day-range-end)]:rounded-r-full',
        ),
        day_button: cn(
          buttonVariants({ variant: 'ghost' }),
          'size-8 rounded-full p-0 font-medium aria-selected:opacity-100',
        ),
        range_start: 'day-range-start',
        range_end: 'day-range-end',
        range_middle:
          'aria-selected:bg-accent aria-selected:text-accent-foreground rounded-none',
        selected:
          'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground',
        today: 'bg-accent text-accent-foreground rounded-full',
        outside: 'day-outside text-muted-foreground aria-selected:text-muted-foreground',
        disabled: 'text-muted-foreground opacity-50',
        hidden: 'invisible',
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation, ...chevronProps }) =>
          orientation === 'left' ? (
            <ChevronLeftIcon className="size-4" {...chevronProps} />
          ) : (
            <ChevronRightIcon className="size-4" {...chevronProps} />
          ),
      }}
      {...props}
    />
  );
}

export { Calendar };
